
import { resolveApiBilingualMessage } from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { cleanPhoneNumber } from "@/app/infrastructures/misc/common-library/Whatsapp";
import { PHONE_NUMBER_HD } from "@/app/infrastructures/misc/Constants";
import { AuthController } from "@/app/ui/controllers/AuthController";
import { VerificationOTPRequest } from "@/data/payload/api/AuthRequest";
import moment from "moment";
import { Options, prop, Vue } from "vue-class-component";

declare const grecaptcha: {
  render: (
    container: string,
    options: { sitekey: string; callback: (e: any) => void }
  ) => number;
  reset: (id?: number) => void;
  execute: (args: any) => void;
};

@Options({
  watch: {
    "$route.name"() {
      if (this.countDownInterval) clearInterval(this.countDownInterval);
      localStorage.removeItem("otp-countdown");
      this.showOTP = false;
      if (this.scriptEl) this.scriptEl.remove();
      delete (window as any).__recaptchaLoaded;
    }
  }
})
export default class OTP extends Vue {
  otp: string[] = [];
  countDown = "01:21.";
  countDownInterval: NodeJS.Timeout | null = null;
  canRequestOTP = true;
  scriptEl: HTMLScriptElement | string = "";
  widgetId: any = "";
  showOTP = false;
  errorOTP = false;
  errorMessageOTP = "";
  loadingVerifyOTP = false;
  cleanPhoneNumber = cleanPhoneNumber;

  get phoneNumberHD() {
    return PHONE_NUMBER_HD
  }

  private load(): Promise<void> {
    if (typeof grecaptcha !== "undefined") return Promise.resolve();
    return new Promise((res, rej) => {
      (window as any).__recaptchaLoaded = () => res();
      this.scriptEl = document.createElement("script");
      this.scriptEl.src =
        "https://www.google.com/recaptcha/api.js?onload=__recaptchaLoaded&render=explicit";
      this.scriptEl.async = true;
      this.scriptEl.defer = true;
      this.scriptEl.onerror = () =>
        rej(new Error("Failed to load reCAPTCHA script"));
      document.head.appendChild(this.scriptEl);
    });
  }

  get isOTPValid() {
    return this.otp.filter((o) => !!o).length === 6;
  }

  get recaptchSiteKey() {
    return process.env.VUE_APP_RECAPTCHA_SITEKEY;
  }

  get token() {
    return this.$route.query.token || "";
  }

  get username() {
    return this.$route.query.username || "";
  }

  get answer() {
    return this.$route.query.answer || "";
  }

  get question() {
    return this.$route.query.question || "";
  }

  get email() {
    return this.$route.query.email || "";
  }

  get emailPartner() {
    return this.$route.query.email_partner || "";
  }

  get phoneNumber() {
    return this.$route.query.phone_number || "";
  }

  created() {
    if (!this.token && !this.question && !this.answer) {
      this.$router.replace("/account/verification-account");
    }
    this.load();
  }

  mounted() {
    this.setOTPCountDown();
  }

  unmounted() {
    if (this.countDownInterval) clearInterval(this.countDownInterval);
    localStorage.removeItem("otp-countdown");
  }

  findNextInput(element: Element): any {
    const nextElement =
      element.parentElement?.parentElement?.nextElementSibling
        ?.firstElementChild?.firstElementChild;

    if (!nextElement) return;
    return nextElement.nodeName === "INPUT"
      ? nextElement
      : this.findNextInput(nextElement);
  }

  findPrevInput(element: Element): any {
    const prevElement =
      element.parentElement?.parentElement?.previousElementSibling
        ?.firstElementChild?.firstElementChild;

    if (!prevElement) return;
    return prevElement.nodeName === "INPUT"
      ? prevElement
      : this.findPrevInput(prevElement);
  }

  moveToPrev(event: any) {
    const prevInput = this.findPrevInput(event.target);
    if (prevInput) {
      prevInput.focus();
      prevInput.select();
    }
  }

  moveToNext(event: any) {
    const nextInput: any = this.findNextInput(event.target);
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  }

  onInput(event: any, index = 0) {
    this.otp[index] = event.target.value;
    this.otp = this.otp.filter((o) => !!o);
    this.errorMessageOTP = "";
    this.errorOTP = false;
    if (event.inputType === "deleteContentBackward") {
      this.moveToPrev(event);
    } else if (
      event.inputType === "insertText" ||
      event.inputType === "deleteContentForward"
    ) {
      this.moveToNext(event);
    }
  }

  onKeyDown(event: any) {
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    switch (event.code) {
      case "ArrowLeft":
        this.moveToPrev(event);
        event.preventDefault();

        break;

      case "ArrowUp":
      case "ArrowDown":
        event.preventDefault();

        break;

      case "Backspace":
        if (event.target.value.length === 0) {
          this.moveToPrev(event);
          event.preventDefault();
        }

        break;

      case "ArrowRight":
        this.moveToNext(event);
        event.preventDefault();

        break;

      case "Enter":
      case "NumpadEnter":
      case "Tab":
        break;

      default:
        if (
          !(
            event.code !== "Space" &&
            Number(event.key) >= 0 &&
            Number(event.key) <= 9
          ) ||
          (this.otp.join("").length >= 6 && event.code !== "Delete")
        ) {
          event.preventDefault();
        }

        break;
    }
  }

  onPaste(event: any) {
    const paste = event.clipboardData.getData("text");

    if (paste.length) {
      const pastedCode = paste.substring(0, 6);

      if (!isNaN(pastedCode)) {
        this.otp = pastedCode.split("");
      }
    }

    event.preventDefault();
  }

  setOTPCountDown() {
    this.canRequestOTP = false;
    const existingCountdown = localStorage.getItem("otp-countdown");
    let nextTwoMinute = new Date(
      moment()
        .add(2, "minutes")
        .toISOString()
    ).getTime();
    if (existingCountdown) {
      nextTwoMinute = Number(existingCountdown);
    }
    localStorage.setItem("otp-countdown", nextTwoMinute.toString());
    this.countDownInterval = setInterval(() => {
      const now = new Date().getTime();
      this.countDown = moment(nextTwoMinute - now).format("mm:ss.");
      if (now >= nextTwoMinute && this.countDownInterval) {
        this.canRequestOTP = true;
        clearInterval(this.countDownInterval);
      }
    }, 1);
  }

  requestOTP() {
    // request OTP
    this.showOTP = true;
    this.widgetId = grecaptcha.render("otp-captcha", {
      sitekey: this.recaptchSiteKey || "",
      callback: (token: any) => {
        AuthController.verificationAccount({
          answer: String(this.answer),
          captchaToken: token,
          emailOrUsername: String(this.username),
          question: String(this.question),
          isFromOTP: true
        }).then((res: any) => {
          this.$router.replace({
            query: {
              ...this.$route.query,
              token: res.token
            }
          });
          localStorage.removeItem("otp-countdown");
          this.setOTPCountDown();
          this.otp = [];
          this.errorOTP = false;
          this.errorMessageOTP = "";
        });
        this.showOTP = false;
        grecaptcha.reset(this.widgetId);
      }
    });
  }

  async verifyOTP() {
    if (!this.isOTPValid || this.loadingVerifyOTP) return;
    try {
      this.errorMessageOTP = "";
      this.errorOTP = false;
      this.loadingVerifyOTP = true;
      const res = await AuthController.verifyOTP(
        new VerificationOTPRequest({
          otp: this.otp.join(""),
          token: String(this.token)
        })
      );

      if (res) {
        this.$router.replace({
          path: "/account/reset-password",
          query: {
            token: String(this.token),
            email: String(this.username)
          }
        });
      } else {
        this.errorOTP = true;
        this.errorMessageOTP = String(
          this.$t("verificationAccount.otp.wrongCode")
        );
      }
    } catch (error) {
      this.errorOTP = true;
      const err = (error as any)?.response;
      const rawMsg = err?.data?.message;
      this.errorMessageOTP =
        resolveApiBilingualMessage(rawMsg) ||
        String(this.$t("verificationAccount.otp.wrongCode"));
    } finally {
      this.loadingVerifyOTP = false;
    }
  }
}
